import React, { useEffect, useRef, useState } from "react";
import { Printer as PrinterIcon, Bluetooth, Wifi, Usb, Save, Printer as SysPrinter, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHeader, Button, Badge } from "@/components/common";

const KEY = "florist_printer_settings";
const DEFAULT = { type: "bluetooth", name: "", address: "", port: 9100, paper: 58, copies: 1, autoPrint: true, openDrawer: false };

// Common ESC/POS BLE serial services (printers expose a writable characteristic under one of these)
const BLE_SERVICES = [
  0x18f0, 0xff00, 0xffe0, 0xfff0, 0xff50,
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "000018f0-0000-1000-8000-00805f9b34fb",
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e", // Nordic UART
  "49535343-fe7d-4ae5-8fa9-9fafd205e455", // ISSC
];

const TYPES = [
  { key: "bluetooth", label: "Bluetooth", icon: Bluetooth, sub: "Printer thermal Bluetooth (ESC/POS) — Chrome/Edge" },
  { key: "usb", label: "USB", icon: Usb, sub: "Printer USB (WebUSB) — Chrome/Edge" },
  { key: "lan", label: "LAN / Wi-Fi", icon: Wifi, sub: "IP printer port 9100 — butuh APK / jaringan lokal" },
  { key: "system", label: "Cetak Sistem", icon: SysPrinter, sub: "Dialog cetak browser / PDF (58mm)" },
];

function enc(str) { return new TextEncoder().encode(str); }
function concat(arrays) {
  const len = arrays.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(len); let o = 0;
  for (const a of arrays) { out.set(a, o); o += a.length; }
  return out;
}
function buildReceipt(store, paper, openDrawer) {
  const ESC = 0x1b, GS = 0x1d;
  const cols = paper === 58 ? 32 : 48;
  const center = (t) => { const pad = Math.max(0, Math.floor((cols - t.length) / 2)); return " ".repeat(pad) + t; };
  const line = "-".repeat(cols);
  const parts = [];
  parts.push(new Uint8Array([ESC, 0x40])); // init
  parts.push(new Uint8Array([ESC, 0x61, 0x01])); // center
  parts.push(enc((store?.store_name || "FloristOS") + "\n"));
  if (store?.store_address) parts.push(enc(store.store_address + "\n"));
  if (store?.store_phone) parts.push(enc(store.store_phone + "\n"));
  parts.push(new Uint8Array([ESC, 0x61, 0x00])); // left
  parts.push(enc(line + "\n"));
  parts.push(enc("TES CETAK PRINTER\n"));
  parts.push(enc("Tanggal : " + new Date().toLocaleString("id-ID") + "\n"));
  parts.push(enc("Kertas  : " + paper + "mm (" + cols + " kolom)\n"));
  parts.push(enc(line + "\n"));
  parts.push(enc(center("*** Berhasil terhubung ***") + "\n"));
  parts.push(enc("\n\n\n"));
  if (openDrawer) parts.push(new Uint8Array([ESC, 0x70, 0x00, 0x19, 0xfa])); // kick drawer
  parts.push(new Uint8Array([GS, 0x56, 0x00])); // full cut
  return concat(parts);
}

export default function Printer() {
  const [s, setS] = useState(() => { try { return { ...DEFAULT, ...JSON.parse(localStorage.getItem(KEY) || "{}") }; } catch { return DEFAULT; } });
  const [store, setStore] = useState(null);
  const [busy, setBusy] = useState(null);
  const btChar = useRef(null);
  const usbDev = useRef(null);
  const btSupported = typeof navigator !== "undefined" && !!navigator.bluetooth;
  const usbSupported = typeof navigator !== "undefined" && !!navigator.usb;

  useEffect(() => { api.get("/settings").then(setStore).catch(() => {}); }, []);
  const update = (patch) => setS((p) => ({ ...p, ...patch }));
  const save = () => { localStorage.setItem(KEY, JSON.stringify(s)); toast.success("Pengaturan printer tersimpan di perangkat/browser ini."); };

  const connectBluetooth = async () => {
    if (!btSupported) return toast.error("Browser ini tidak mendukung Web Bluetooth. Gunakan Chrome/Edge di desktop/Android.");
    setBusy("bt");
    try {
      const device = await navigator.bluetooth.requestDevice({ acceptAllDevices: true, optionalServices: BLE_SERVICES });
      const server = await device.gatt.connect();
      const services = await server.getPrimaryServices();
      let writable = null;
      for (const svc of services) {
        const chars = await svc.getCharacteristics();
        writable = chars.find((c) => c.properties.write || c.properties.writeWithoutResponse);
        if (writable) break;
      }
      if (!writable) throw new Error("Tidak menemukan karakteristik tulis (printer ESC/POS) di perangkat ini.");
      btChar.current = writable;
      device.addEventListener("gattserverdisconnected", () => { btChar.current = null; });
      update({ type: "bluetooth", name: device.name || "Bluetooth Printer", address: device.id || "" });
      toast.success(`Terhubung: ${device.name || "printer"}. Jangan lupa Simpan.`);
    } catch (e) { if (e.name !== "NotFoundError") toast.error(e.message || "Gagal menghubungkan Bluetooth"); }
    finally { setBusy(null); }
  };

  const connectUSB = async () => {
    if (!usbSupported) return toast.error("Browser ini tidak mendukung WebUSB. Gunakan Chrome/Edge di desktop/Android.");
    setBusy("usb");
    try {
      const device = await navigator.usb.requestDevice({ filters: [{ classCode: 7 }] });
      await device.open();
      if (device.configuration === null) await device.selectConfiguration(1);
      const iface = device.configuration.interfaces.find((i) => i.alternate.interfaceClass === 7) || device.configuration.interfaces[0];
      await device.claimInterface(iface.interfaceNumber);
      usbDev.current = { device, iface: iface.interfaceNumber };
      update({ type: "usb", name: device.productName || "USB Printer", address: `${device.vendorId}:${device.productId}` });
      toast.success(`Terhubung: ${device.productName || "printer USB"}. Jangan lupa Simpan.`);
    } catch (e) { if (e.name !== "NotFoundError") toast.error(e.message || "Gagal menghubungkan USB"); }
    finally { setBusy(null); }
  };

  const writeBytes = async (bytes) => {
    if (s.type === "bluetooth") {
      if (!btChar.current) throw new Error("Belum terhubung ke printer Bluetooth. Tekan 'Hubungkan Printer'.");
      const CHUNK = 180;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        const slice = bytes.slice(i, i + CHUNK);
        if (btChar.current.properties.writeWithoutResponse) await btChar.current.writeValueWithoutResponse(slice);
        else await btChar.current.writeValue(slice);
      }
    } else if (s.type === "usb") {
      if (!usbDev.current) throw new Error("Belum terhubung ke printer USB. Tekan 'Hubungkan Printer'.");
      const { device, iface } = usbDev.current;
      const ep = device.configuration.interfaces.find((i) => i.interfaceNumber === iface).alternate.endpoints.find((e) => e.direction === "out");
      await device.transferOut(ep.endpointNumber, bytes);
    }
  };

  const printSystem = () => {
    const w = s.paper === 58 ? "58mm" : "80mm";
    const html = `<!doctype html><html><head><meta charset="utf-8"><style>
      @page{size:${w} auto;margin:0}
      body{width:${w};margin:0;padding:4px 6px;font-family:'Courier New',monospace;font-size:11px;line-height:1.35;color:#000}
      .c{text-align:center}.b{font-weight:bold}.line{border-top:1px dashed #000;margin:4px 0}
    </style></head><body>
      <div class="c b">${store?.store_name || "FloristOS"}</div>
      ${store?.store_address ? `<div class="c">${store.store_address}</div>` : ""}
      ${store?.store_phone ? `<div class="c">${store.store_phone}</div>` : ""}
      <div class="line"></div>
      <div class="b">TES CETAK PRINTER</div>
      <div>Tanggal: ${new Date().toLocaleString("id-ID")}</div>
      <div>Kertas : ${s.paper}mm</div>
      <div class="line"></div>
      <div class="c">*** Cetak Sistem berhasil ***</div>
    </body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed"; iframe.style.right = "0"; iframe.style.bottom = "0"; iframe.style.width = "0"; iframe.style.height = "0"; iframe.style.border = "0";
    document.body.appendChild(iframe);
    iframe.contentDocument.open(); iframe.contentDocument.write(html); iframe.contentDocument.close();
    iframe.contentWindow.focus();
    setTimeout(() => { iframe.contentWindow.print(); setTimeout(() => document.body.removeChild(iframe), 1000); }, 300);
  };

  const testPrint = async () => {
    save();
    if (s.type === "system") { printSystem(); return; }
    if (s.type === "lan") { toast.message("Cetak LAN tidak bisa langsung dari browser. Simpan IP, gunakan APK/jembatan lokal untuk cetak LAN."); return; }
    setBusy("test");
    try {
      const bytes = buildReceipt(store, s.paper, s.openDrawer);
      for (let i = 0; i < (s.copies || 1); i++) await writeBytes(bytes);
      toast.success("Tes cetak terkirim ke printer.");
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  return (
    <>
      <PageHeader title="Printer Struk Thermal" subtitle="ESC/POS · Bluetooth · USB · LAN · 58mm" icon={PrinterIcon} />

      {(!btSupported || !usbSupported) && (
        <Card className="p-4 mb-5 bg-amber-50 border-amber-200">
          <div className="flex gap-2 text-sm text-amber-800"><AlertTriangle size={18} className="shrink-0" />
            <span>Koneksi langsung {(!btSupported && "Bluetooth") || ""}{(!btSupported && !usbSupported) ? " & " : ""}{(!usbSupported && "USB") || ""} hanya didukung di <b>Chrome/Edge</b> (desktop/Android) melalui HTTPS. Di browser lain gunakan <b>Cetak Sistem</b> atau aplikasi APK.</span>
          </div>
        </Card>
      )}

      <Card className="p-5 mb-5">
        <h3 className="font-semibold text-slate-800 mb-3">Jenis Koneksi</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {TYPES.map((t) => (
            <button key={t.key} onClick={() => update({ type: t.key })} data-testid={`printer-type-${t.key}`}
              className={`text-left p-3 rounded-xl border transition ${s.type === t.key ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"}`}>
              <t.icon size={20} className={s.type === t.key ? "text-primary" : "text-slate-400"} />
              <div className={`font-medium text-sm mt-2 ${s.type === t.key ? "text-primary" : "text-slate-800"}`}>{t.label}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">{t.sub}</div>
            </button>
          ))}
        </div>
      </Card>

      {(s.type === "bluetooth" || s.type === "usb") && (
        <Card className="p-5 mb-5">
          <h3 className="font-semibold text-slate-800 mb-3">Deteksi & Hubungkan Printer</h3>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={s.type === "bluetooth" ? connectBluetooth : connectUSB} disabled={busy === "bt" || busy === "usb"} data-testid="printer-connect">
              {s.type === "bluetooth" ? <Bluetooth size={16} /> : <Usb size={16} />} {busy === "bt" || busy === "usb" ? "Memindai..." : "Hubungkan Printer"}
            </Button>
            {s.name ? <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 size={13} className="inline mr-1" />{s.name}</Badge> : <span className="text-sm text-slate-400">Belum ada printer dipilih</span>}
          </div>
          <p className="text-xs text-slate-400 mt-3">Pilih printer thermal Anda dari dialog browser. Pastikan printer menyala & dalam mode pairing (Bluetooth) atau tersambung kabel (USB).</p>
        </Card>
      )}

      {s.type === "lan" && (
        <Card className="p-5 mb-5">
          <h3 className="font-semibold text-slate-800 mb-3">Alamat Printer LAN</h3>
          <div className="grid grid-cols-3 gap-3">
            <label className="col-span-2 block"><span className="text-sm font-medium text-slate-600 mb-1 block">IP Printer</span>
              <input value={s.address} onChange={(e) => update({ address: e.target.value.trim() })} placeholder="192.168.1.50" data-testid="printer-lan-ip" className="w-full h-10 px-3 rounded-xl border border-slate-300 outline-none focus:border-primary" /></label>
            <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Port</span>
              <input value={s.port} onChange={(e) => update({ port: Number(String(e.target.value).replace(/[^0-9]/g, "")) || 9100 })} placeholder="9100" data-testid="printer-lan-port" className="w-full h-10 px-3 rounded-xl border border-slate-300 outline-none focus:border-primary" /></label>
          </div>
          <p className="text-xs text-amber-600 mt-3">Catatan: browser tidak dapat mencetak langsung ke IP printer di jaringan lokal. Konfigurasi ini tersimpan & dipakai oleh aplikasi APK / jembatan cetak lokal.</p>
        </Card>
      )}

      <Card className="p-5 mb-5">
        <h3 className="font-semibold text-slate-800 mb-3">Struk</h3>
        <div className="space-y-4">
          <div>
            <span className="text-sm font-medium text-slate-600 mb-2 block">Lebar kertas</span>
            <div className="flex gap-2">
              {[58, 80].map((p) => <button key={p} onClick={() => update({ paper: p })} data-testid={`printer-paper-${p}`} className={`px-4 h-9 rounded-full text-sm font-medium ${s.paper === p ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{p} mm</button>)}
            </div>
          </div>
          <div>
            <span className="text-sm font-medium text-slate-600 mb-2 block">Jumlah salinan</span>
            <div className="flex gap-2">
              {[1, 2, 3].map((c) => <button key={c} onClick={() => update({ copies: c })} data-testid={`printer-copies-${c}`} className={`px-4 h-9 rounded-full text-sm font-medium ${s.copies === c ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{c}x</button>)}
            </div>
          </div>
          <label className="flex items-center justify-between gap-3 py-1">
            <span><span className="block font-medium text-slate-800 text-sm">Cetak struk otomatis</span><span className="block text-xs text-slate-400">Langsung cetak saat transaksi kasir sukses</span></span>
            <input type="checkbox" checked={s.autoPrint} onChange={(e) => update({ autoPrint: e.target.checked })} data-testid="printer-autoprint" className="w-5 h-5 accent-[hsl(var(--primary))]" />
          </label>
          <label className="flex items-center justify-between gap-3 py-1">
            <span><span className="block font-medium text-slate-800 text-sm">Buka cash drawer</span><span className="block text-xs text-slate-400">Kirim pulsa drawer setelah cetak (ESC/POS)</span></span>
            <input type="checkbox" checked={s.openDrawer} onChange={(e) => update({ openDrawer: e.target.checked })} data-testid="printer-drawer" className="w-5 h-5 accent-[hsl(var(--primary))]" />
          </label>
        </div>
      </Card>

      <div className="flex flex-wrap gap-3 mb-10">
        <Button variant="outline" onClick={testPrint} disabled={busy === "test"} data-testid="printer-test"><PrinterIcon size={16} /> {busy === "test" ? "Mengirim..." : "Tes Cetak"}</Button>
        <Button onClick={save} data-testid="printer-save"><Save size={16} /> Simpan Pengaturan</Button>
      </div>
    </>
  );
}
